import React, { useCallback, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { WithRouterProps } from 'decorators/withRouter';
import { StoreProps } from 'store';

import TopPanelContainer from 'components/topPanel';
import DataListContainer from 'components/dataListContainer';
import { Column, Row } from 'components/ui/table';
import Button from 'components/ui/button';

import getConfigurationByName from 'selectors/getConfigurationByName';
import quickFiltersConfig from './quickFiltersConfig';
import tableNames from 'constants/tableNames';
import { AnyObject } from 'types/Common';
import PageTemplate from 'components/pageTemplate/PageTemplate';
import { deleteSettlement, getSettlementList } from 'api/settlements';
import { openModal } from 'actions/modal';
import './style.scss';
import { changeDataForceUpdate } from 'actions/getData';
import { ROLE } from 'helpers/roles';
import { SelectInterface } from './types';

interface ConnectedProps {
  configuration: AnyObject;
  settlementBalanceUser: SelectInterface;
  statuses: SelectInterface,
  userMerchant: SelectInterface;
  hasSettlementAddAbility: boolean,
  canEditAccess: boolean
}

type Props = ConnectedProps & StoreProps & IntlProps & WithRouterProps;

const SettlementListContainer = (props: Props) => {
  const {
    configuration,
    getTranslate,
    dispatch,
    statuses,
    userMerchant,
    settlementBalanceUser,
    hasSettlementAddAbility,
    canEditAccess,
  } = props;

  const openCreateModal = useCallback((content?) => {
    dispatch(
      openModal({
        modalId: 'UpdateSettlementData',
        content,
      })
    );
  }, [dispatch]);

  const openDeleteModal = useCallback(async (id: number) => {
    dispatch(
      openModal({
        modalId: 'Confirm',
        callback: (answer) => {
          if (answer) {
            deleteSettlement(id);
            dispatch(changeDataForceUpdate(tableNames.settlements, true));
          }
        },
        content: { text: getTranslate('settlements.modal.warningDelete.message') },
      })
    );
  }, [dispatch, getTranslate]);

  const renderRow = useCallback((data) => {
    return (
      <Row>
        {configuration.map((config) => {
          const { id } = config;
          let content = data[id];

          if (id === 'status') {
            content = statuses.list.find(({ id }) => id === content)?.text || data[id];
          }

          if (id === 'balanceId') {
            content = settlementBalanceUser.list.find(({ id }) => id === content)?.text || data[id];
          }

          if (id === 'merchantId') {
            content = userMerchant.list.find(({ id }) => id === content)?.text || data[id];
          }

          if (id === 'edit' && canEditAccess) {
            content = (
              <>
                <Button
                  icon='im-Edit1'
                  status='transparent'
                  size='inline'
                  onClick={() => openCreateModal(data)}
                />
                <Button
                  icon='im-Basket'
                  status='transparent'
                  size='inline'
                  onClick={() => openDeleteModal(data.id)}
                />
              </>
            );
          }

          return (
            <Column
              columnWidths={data.columnWidths}
              key={id}
              id={id}
              content={content || '—'}
              customClass={classNames(`ui-table__column_${id}`, {
                'ui-table__column_fixed': config.fixed,
              })}
              params={{
                ...config,
                valueId: config.id,
                valueType: config.valueType,
              }}
            />
          );
        })}
      </Row>
    );
  }, [configuration, statuses, canEditAccess, openDeleteModal, openCreateModal, userMerchant, settlementBalanceUser]);

  return (
    <PageTemplate.Main topPadding>
      <PageTemplate.Container customClass={'page-settlements'}>
        <TopPanelContainer
          name={tableNames.settlements}
          isFilterButton={false}
          isConfigurationButton={true}
          button={
              hasSettlementAddAbility && (
                <Button
                  status='primary'
                  onClick={openCreateModal}
                  text={getTranslate('settlements.modal.buttons.addSettlement')}
                  customClass='settlements-create-button'
                />
              )
          }
          filtersConfig={quickFiltersConfig(canEditAccess)}
        />
        <DataListContainer
          tableName={tableNames.settlements}
          customClass='table-settlements'
          apiRequest={getSettlementList}
          rowRender={renderRow}
        />
      </PageTemplate.Container>
    </PageTemplate.Main>
  );
};

const mapStateToProps = (state): ConnectedProps => ({
  configuration: getConfigurationByName(state, tableNames.settlements),
  settlementBalanceUser: {
    list: state.filtersValues?.settlementBalanceUser?.list || [],
    isLoading: state.filtersValues?.settlementBalanceUser?.isLoading
  },
  statuses: {
    list: state.filtersValues?.settlementStatus?.list || [],
    isLoading: state.filtersValues?.settlementStatus?.isLoading
  },
  userMerchant: {
    list: state.filtersValues?.userMerchant?.list || [],
    isLoading: state.filtersValues?.userMerchant?.isLoading
  },
  hasSettlementAddAbility: state.user.hasSettlementAddAbility,
  canEditAccess: state.user.userRoles.some(role => [
    ROLE.INTERNAL_ADMIN,
    ROLE.INTERNAL_SUPPORT,
  ].includes(role))
});

export default withRouter(
  connect(mapStateToProps)(addTranslation(SettlementListContainer))
);
